import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import App from "./App";
import "./index.css";

/**
 * Client entry.
 *
 * Two responsibilities:
 *  1. Hydrate the SSR markup if `dist/index.html` was prerendered by
 *     `scripts/prerender.mjs`, otherwise fall back to a client-only mount.
 *  2. Attach the IntersectionObserver-driven Soft-Bloom reveals only after
 *     hydration, so the static HTML never depends on JS for visibility.
 */
const container = document.getElementById("root");
if (!container) {
  throw new Error("Root container #root not found");
}

const root = (
  <StrictMode>
    <App />
  </StrictMode>
);

if (container.hasChildNodes()) {
  hydrateRoot(container, root);
} else {
  createRoot(container).render(root);
}

// Progressive-enhancement: only init reveal animations in the browser,
// and only when the user hasn't opted out of motion.
//
// `.js` is added inside this microtask (rather than synchronously after
// the hydrate/createRoot call) so we do not flip the
// `.js .bloom-on-scroll { opacity: 0 }` CSS rule on until the
// IntersectionObserver is wired up and ready to add `.is-visible`. Without
// this, all reveal targets would sit at `opacity: 0` for one frame between
// the synchronous `.js` set and the first IO callback, producing a brief
// "flash off, flash on" flicker on first paint.
//
// Defense in depth: if the bundle ever fails to load (404, blocked, syntax
// error) the inline HTML script in `index.html` does NOT add `.js`, so the
// SSR markup stays fully visible instead of going blank.
queueMicrotask(() => {
  document.documentElement.classList.add("js");

  if (typeof window === "undefined") return;

  const reduceMotion =
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  const targets = document.querySelectorAll<HTMLElement>(".bloom-on-scroll");

  if (reduceMotion || !("IntersectionObserver" in window)) {
    targets.forEach((el) => el.classList.add("is-visible"));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: "0px 0px -60px 0px" },
  );
  targets.forEach((el) => observer.observe(el));
});
